import React from "react";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { PersonPlusFill, PersonFill } from "react-bootstrap-icons";
import { MATCH_PHONE, MATCH_EXACT, MATCH_CLOSE } from "./scanNameMatch";

/**
 * Where the sheet about to be confirmed is going.
 *
 * Duplicates are the failure mode of a batch, not a rare edge: a shop's filing
 * cabinet is full of its regulars, and the same person turns up three times.
 * Matching on a name alone can never be certain, so this shows the candidates
 * and makes the operator say which one -- the sheet is in their hand and they
 * know whether this is the Branden Deremer who already has an account.
 *
 * A new bowler is always offered and is never the quiet default when someone
 * plausible exists: reusing is the recoverable mistake, and creating a second
 * record for a regular is the one nobody notices.
 */

const Box = styled.div`
  background: ${(p) => p.theme.color.surfaceAlt};
  border-radius: ${(p) => p.theme.radius.md};
  padding: ${(p) => p.theme.spacing.md};
  margin-bottom: ${(p) => p.theme.spacing.md};
  font-family: ${(p) => p.theme.font.family};
`;

const Title = styled.div`
  font-size: ${(p) => p.theme.font.sizeSm};
  font-weight: ${(p) => p.theme.font.weightSemibold};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${(p) => p.theme.color.textMuted};
  margin-bottom: ${(p) => p.theme.spacing.sm};
`;

const Choice = styled.label`
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.spacing.sm};
  /* Touch-first: these get tapped on an iPad at a shop counter. */
  min-height: 44px;
  padding: ${(p) => p.theme.spacing.xs} ${(p) => p.theme.spacing.sm};
  border-radius: ${(p) => p.theme.radius.sm};
  cursor: pointer;
  margin-bottom: 2px;
  background: ${(p) => (p.$on ? p.theme.color.surface : "transparent")};
  border: 1px solid
    ${(p) => (p.$on ? p.theme.color.primary : "transparent")};
`;

const Who = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  font-size: ${(p) => p.theme.font.sizeMd};
  color: ${(p) => p.theme.color.text};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Aside = styled.div`
  font-size: ${(p) => p.theme.font.sizeSm};
  color: ${(p) => p.theme.color.textMuted};
`;

const Why = styled.span`
  font-size: ${(p) => p.theme.font.sizeSm};
  white-space: nowrap;
  color: ${(p) =>
    p.$strong ? p.theme.color.success : p.theme.color.textMuted};
`;

const fullName = (c) => `${c.firstname || ""} ${c.lastname || ""}`.trim();

const REASON_KEY = {
  [MATCH_PHONE]: "specsheetScan.batch.matchPhone",
  [MATCH_EXACT]: "specsheetScan.batch.exactMatch",
  [MATCH_CLOSE]: "specsheetScan.batch.matchClose",
};

const BatchDestination = ({
  scannedName,
  scannedPhone,
  candidates = [],
  searching = false,
  exactId = "",
  value,
  onChange,
}) => {
  const { t } = useTranslation();

  return (
    <Box>
      <Title>{t("specsheetScan.batch.destination")}</Title>

      {searching && (
        <Aside>
          <Spinner animation="border" size="sm" />{" "}
          {t("specsheetScan.batch.searching")}
        </Aside>
      )}

      {candidates.map((c) => (
        <Choice key={c.id} $on={value === c.id}>
          <Form.Check
            type="radio"
            name="batch-destination"
            checked={value === c.id}
            onChange={() => onChange(c.id)}
          />
          <PersonFill size={16} />
          <Who>{fullName(c)}</Who>
          {REASON_KEY[c.reason] && (
            <Why $strong={c.id === exactId}>{t(REASON_KEY[c.reason])}</Why>
          )}
          {c.telephone ? <Aside>{c.telephone}</Aside> : null}
        </Choice>
      ))}

      <Choice $on={!value}>
        <Form.Check
          type="radio"
          name="batch-destination"
          checked={!value}
          onChange={() => onChange("")}
        />
        <PersonPlusFill size={16} />
        <Who>
          {t("specsheetScan.batch.newBowler", {
            name: scannedName || t("specsheetScan.batch.noName"),
          })}
        </Who>
      </Choice>

      {!searching && candidates.length === 0 && (
        <Aside>
          {scannedPhone
            ? t("specsheetScan.batch.noCandidatesWithPhone")
            : t("specsheetScan.batch.noCandidates")}
        </Aside>
      )}
    </Box>
  );
};

export default BatchDestination;
